import { Card, Stack, Box, Typography, Chip, Grid, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import Page from "../Page";
import { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList } from "src/hooks/useFetch.hook";
import { BaseTable } from "../BaseTable/BaseTable";
import { userListColumns } from "./user.constant";
import { commonSlice } from "src/store/common/common.slice";
import { useNavigate } from "react-router-dom";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { Icon } from "@iconify/react";
import peopleFill from "@iconify/icons-eva/people-fill";
import checkmarkCircle2Fill from "@iconify/icons-eva/checkmark-circle-2-fill";
import closeCircleFill from "@iconify/icons-eva/close-circle-fill";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.primary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter}08 0%, ${theme.palette.primary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'visible',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 25px ${theme.palette.primary.light}20`,
    border: `1px solid ${theme.palette.primary.light}60`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
    borderRadius: `${theme.shape.borderRadiusMd}px ${theme.shape.borderRadiusMd}px 0 0`,
  },
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: `linear-gradient(135deg, ${theme.palette.primary.lighter} 0%, ${theme.palette.background.paper} 100%)`,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.lighter}05 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.customShadows.z12,
    border: `1px solid ${theme.palette.primary.light}`,
    '&::before': {
      opacity: 1,
    }
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
}));

const SearchSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: theme.palette.background.paper,
}));

export default function CMCustomerList() {
  const hasCreateAccess = useAccess(FEATURE.CUSTOMERS, "C");
  const { data: customers, isFetching } = useList("cmCustomers");
  const isReadOnly = useAccess(FEATURE.CUSTOMERS, "R");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState();

  // Calculate statistics
  const totalDrivers = customers?.length || 0;
  const activeDrivers = customers?.filter(customer => customer?.active === "Y")?.length || 0;
  const inactiveDrivers = customers?.filter(customer => customer?.active === "N")?.length || 0;

  const rowOrCellClickRedirect = (data) => {
    if (isReadOnly) return;
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: data.id,
        tabOpenedFor: "customer",
        tabOpened: "general",
      })
    );

    navigate(
      `/cm-customer/${data?.row?.id}`
    );
    
  };

  const addNewCustomerBtnHandler = () =>
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "customer",
        tabOpened: "general",
      })
    );

  useEffect(() => {
    if (!isFetching && customers) {
      let data = [...customers];
      setTableData(data);
    }
  }, [isFetching, customers]);

  const handleSearching = (event) => {
    const dataToSearchFrom = customers || [];
    const searchValue = event.target.value.trim().toString().toLowerCase();
    if (searchValue) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "fullName",
            "email",
            "phone",
            "active",
            "createdAt",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "createdAt"
                ? yearFirstDateTimeFormat(value).toString()
                : value === "Y"
                ? "enabled"
                : value === "N"
                ? "disabled"
                : value
                ? value.toString().toLowerCase()
                : ""
            );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchValue)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
    } else setTableData([...(customers || [])]);
  };

  return (
    <Page>
      <StyledListCard>
        {/* Header Section with Statistics */}
        <HeaderSection>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Box sx={{ position: 'relative' }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  color: 'transparent',
                  background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.dark} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                  letterSpacing: '-0.25px',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                CM Customers
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Manage and monitor your charge management customers
              </Typography>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -6,
                  left: 0,
                  width: '60px',
                  height: '3px',
                  background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  borderRadius: '2px',
                }}
              />
            </Box>
            <Chip
              icon={<Icon icon={peopleFill} />}
              label={`${totalDrivers} Total Customers`}
              color="primary"
              variant="outlined"
              sx={{ 
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 2,
                py: 1,
                height: 'auto',
                '& .MuiChip-icon': {
                  fontSize: '1.1rem'
                }
              }}
            />
          </Stack>

          {/* Statistics Cards */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard>
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '50%',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: 'white',
                      boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}40`,
                    }}
                  >
                    <Icon icon={peopleFill} width={28} height={28} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'text.primary',
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {totalDrivers}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                      }}
                    >
                      Total Customers
                    </Typography>
                  </Box>
                </Stack>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard>
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '50%',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                      color: 'white',
                      boxShadow: (theme) => `0 4px 12px ${theme.palette.success.main}40`,
                    }}
                  >
                    <Icon icon={checkmarkCircle2Fill} width={28} height={28} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'text.primary',
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {activeDrivers}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                      }}
                    >
                      Active Customers
                    </Typography>
                  </Box>
                </Stack>
              </StatsCard>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatsCard>
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '50%',
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
                      color: 'white',
                      boxShadow: (theme) => `0 4px 12px ${theme.palette.error.main}40`,
                    }}
                  >
                    <Icon icon={closeCircleFill} width={28} height={28} />
                  </Box>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 700, 
                        color: 'text.primary',
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        lineHeight: 1.2,
                      }}
                    >
                      {inactiveDrivers}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontSize: '0.75rem',
                      }}
                    >
                      Inactive Customers
                    </Typography>
                  </Box>
                </Stack>
              </StatsCard>
            </Grid>
          </Grid>
        </HeaderSection>

        {/* Search Section */}
        <SearchSection>
          <CommonSearchbar
            placeholder="Search customers by name, email, phone, or status..."
            searchFunc={(e) => handleSearching(e)}
          />
        </SearchSection>

        {/* Table Section */}
        <BaseTable
          rows={tableData || []}
          columns={userListColumns(isReadOnly)}
          loading={isFetching}
          getRowId={(row) => row?.id || row?.customer_id || Math.random()}
          pageSize={10}
          showExportCsvBtn={false}
          csvMorphemes="ev_drivers"
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          isReadOnly={isReadOnly}
          csvColumns={userListColumns(isReadOnly)}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: (theme) => theme.palette.primary.lighter,
                borderBottom: (theme) => `2px solid ${theme.palette.primary.main}`,
              },
              '& .MuiDataGrid-columnHeader': {
                fontWeight: 600,
                color: (theme) => theme.palette.primary.main,
                fontSize: '0.875rem',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                }
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
              }
            }
          }}
        />
      </StyledListCard>
    </Page>
  );
}
